/**
 * @author      luis.orihuela@zumper.com (Luis Orihuela)
 * @copyright   Copyright (c) 2025, Zumper
 * @description messages selectors
 */

import type { Media } from '@zcode/bizos'
import { MediaType } from '@zcode/enums'
import { constructUrl, mediaUrl } from '@zcode/nr-utils'
import type { Building, Listing, State } from '@zcode/state-type'
import { compose } from 'redux'

export interface ImageProps {
  dpr?: number
  readonly height: number
  readonly width: number
}

const selectMessages = (state: State) => state.messages

export const selectStreamTokenData = compose((state) => state.streamToken.data, selectMessages)
export const selectStreamTokenRetry = compose(
  (state) => state.streamToken.retryAfter,
  selectMessages
)
export const selectStreamFeatureNotEnabled = compose(
  (state) => state.streamToken.featureNotEnabled,
  selectMessages
)
export const selectStreamTokenIsError = compose(
  (state) => state.streamToken.isError,
  selectMessages
)
export const selectStreamUnreadCount = compose(
  (state) => state.streamUnreadCounts.data,
  selectMessages
)
export const selectStreamConversations = compose(
  (state) => state.streamConversations.data,
  selectMessages
)

export const selectStreamConversationError = (state: State, streamChannelId: string) => {
  const conversations = selectStreamConversations(state)
  return !!streamChannelId && conversations[streamChannelId]?.meta?.isError
}

export const selectIsStreamConversationClaimed = (state: State, streamChannelId?: string) => {
  const isError = !!streamChannelId && selectStreamConversationError(state, streamChannelId)
  return isError
}

export const selectStreamApiKey = compose((state) => state?.apiKey, selectStreamTokenData)
export const selectStreamToken = compose((state) => state?.token, selectStreamTokenData)
export const selectStreamId = compose((state) => state?.streamId, selectStreamTokenData)

export const selectListings = compose((state) => state.propertyDetails.listing, selectMessages)
export const selectBuildings = compose((state) => state.propertyDetails.building, selectMessages)
export const selectBuilding = (state: State, buildingId: string) => {
  const listings = selectBuildings(state)
  return listings[buildingId]
}
export const selectBuildingIsLoaded = (state: State, buildingId: string) => {
  const building = selectBuilding(state, buildingId)
  return !!building
}
export const selectListingIsLoaded = (state: State, listingId: string) => {
  const listing = selectListing(state, listingId)
  return !!listing
}

export const selectListing = (state: State, listingId: string) => {
  const listings = selectListings(state)
  return listings[listingId]
}

export const selectImageUrl = ({ dpr, height, width }: ImageProps) => {
  return (identifier?: Media | undefined) => {
    if (identifier) {
      if (!dpr) {
        dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
      }
      return constructUrl(mediaUrl(identifier.id, '1280x960'), {
        dpr,
        h: height,
        w: width,
        fit: 'crop',
      })
    }
  }
}

const selectChatImageUrl = selectImageUrl({ height: 208, width: 312 })
const validChatMediaTypes = [MediaType.IMAGE, MediaType.LAYOUT]
export const selectValidChatMedia = (medias: readonly Media[] | undefined) => {
  const firstMedia = medias?.find(({ isAvailable, mediaType }) => {
    return isAvailable && mediaType && validChatMediaTypes.includes(mediaType)
  })
  return selectChatImageUrl(firstMedia)
}

const selectListingImageUrl = (media: readonly Media[] | undefined) => {
  return media && selectValidChatMedia(media)
}
const selectBuildingImageUrl = (media: readonly Media[] | undefined) => {
  return media && selectValidChatMedia(media)
}
export const selectImageUrlByBuildingId = (state: State, buildingId: string) => {
  const building = selectBuilding(state, buildingId)
  return building.media && selectValidChatMedia(building.media)
}

export const getListingDetails = (listing: Listing, id: string) => {
  const {
    agent,
    amenities,
    address,
    bathrooms,
    bedrooms,
    city,
    cityStateText,
    description,
    features,
    isActive,
    lat,
    lng,
    media,
    maxPrice,
    minPrice,
    propertyType,
    state,
    phone: propertyPhone,
  } = listing

  const phone = propertyPhone ?? agent?.phone

  return {
    amenities,
    address: address ?? undefined,
    bathrooms,
    bedrooms,
    city,
    description: description ?? undefined,
    features: features ?? undefined,
    image: selectListingImageUrl(media),
    lat,
    lng,
    maxPrice: maxPrice ?? undefined,
    minPrice: minPrice ?? undefined,
    propertyType: propertyType ?? undefined,
    state,
    cityAndState: cityStateText ?? undefined,
    phone,
    // the "url" listing attribute links to a permanent listing, which means it could be a
    // different listing than the one this conversation is actually about. So we construct a
    // "b"-type link, which will always go to *this* listing.
    // TODO(ZUM-39430): The /r/{id}b link is slightly nicer, because it'll redirect to
    // a more descriptive url like /listings/1234b/2-bedroom-rexburg-id, but it
    // has a bug that sometimes displays the wrong listing. So we're using
    // /listings here until that's fixed.
    // url: `/r/${id}b`,

    url: `/listings/${id}b`,
    active: isActive,
  }
}

export const selectMessaging3ListingDetails = (reduxState: State, id: string) => {
  const listing = selectListing(reduxState, id)
  return getListingDetails(listing, id)
}

export const getBuildingDetails = (building: Building, id: string) => {
  const {
    agent,
    address,
    cityStateText,
    description,
    isActive,
    maxBathrooms,
    maxBedrooms,
    maxPrice,
    media,
    minBathrooms,
    minBedrooms,
    minPrice,
    propertyType,
    phone: propertyPhone,
  } = building
  const phone = propertyPhone ?? agent?.phone
  return {
    address,
    cityAndState: cityStateText ?? undefined,
    description: description ?? undefined,
    image: selectBuildingImageUrl(media),
    maxBathrooms,
    maxBedrooms,
    maxPrice,
    minBathrooms,
    minBedrooms,
    minPrice,
    phone,
    propertyType,
    url: `/b/b${id}`,
    active: isActive,
  }
}

export const selectMessaging3BuildingDetails = (state: State, id: string) => {
  const building = selectBuilding(state, id)
  return getBuildingDetails(building, id)
}
